'use client'

import { useState, useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import Link from 'next/link'
import { Share } from 'lucide-react'
import type { InspirationPiece, InspirationBook } from '@/lib/inspiration'
import { CenteredDialog } from '@/components/CenteredDialog'
import { CircleChevron } from '@/components/CircleChevron'

type InspirationMainContentProps = {
  allPieces: InspirationPiece[]
  books: InspirationBook[]
}

// Helper to get random piece with its book
function getRandomPieceAndBook(pieces: InspirationPiece[], books: InspirationBook[]) {
  const randomIndex = Math.floor(Math.random() * pieces.length)
  const piece = pieces[randomIndex]
  const book = books.find(b => b.id === piece.book_id)!
  return { piece, book }
}

export function InspirationMainContent({ allPieces, books }: InspirationMainContentProps) {
  // Initialize with a random piece
  const initial = useMemo(() => getRandomPieceAndBook(allPieces, books), [])

  const [piece, setPiece] = useState(initial.piece)
  const [book, setBook] = useState(initial.book)
  const [isExpanded, setIsExpanded] = useState(false)
  const [showCopiedFeedback, setShowCopiedFeedback] = useState(false)
  const [showSignupDialog, setShowSignupDialog] = useState(false)

  // Instant client-side random selection
  const handleRefresh = () => {
    const { piece: newPiece, book: newBook } = getRandomPieceAndBook(allPieces, books)
    setPiece(newPiece)
    setBook(newBook)
    setIsExpanded(false) // Collapse when showing new piece
  }

  const handleShare = async () => {
    const shareUrl = `https://homiehub.co/inspiration/${book.slug}/${piece.slug}`
    try {
      await navigator.clipboard.writeText(shareUrl)
      setShowCopiedFeedback(true)
      setTimeout(() => setShowCopiedFeedback(false), 2000)
    } catch (err) {
      const textArea = document.createElement('textarea')
      textArea.value = shareUrl
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      setShowCopiedFeedback(true)
      setTimeout(() => setShowCopiedFeedback(false), 2000)
    }
  }


  return (
    <div className="app-form px-xxl pt-[32px] pb-[32px]">
      <div className="mb-xxl">
        {/* Quote/Content Display */}
        <div className="mb-m pt-xs">
          {piece.quote ? (
            // Quote Type
            <>
              {piece.title && (
                <h2 className="text-xl font-bold text-text-primary mb-m leading-medium">
                  {piece.title}
                </h2>
              )}
              {piece.summary && (
                <p className="text-l text-text-primary mb-m leading-medium">
                  {piece.summary}
                </p>
              )}
              <blockquote className="border-l-[4px] border-stroke-light pl-[14px] py-sm my-m">
                <p className="text-l text-text-primary leading-relaxed italic">
                  {piece.quote}
                </p>
              </blockquote>
              <p className="text-m text-text-inactive leading-medium mt-m">
                — {piece.quote_by || book.title}
              </p>
            </>
          ) : (
            // Concept Type
            <>
              {piece.title && (
                <h2 className="text-xl font-bold text-text-primary mb-m leading-medium">
                  {piece.title}
                </h2>
              )}
              {piece.summary && (
                <p className="text-l text-text-primary mb-l leading-medium">
                  {piece.summary}
                </p>
              )}
              <p className="text-m text-text-inactive mb-m leading-medium">
                — {book.title}
              </p>
            </>
          )}
        </div>

        {/* Action Buttons Row */}
        <div className="flex items-center gap-m mb-m pt-l">
          {/* Read More/Less Button */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center justify-center gap-m py-m pr-l cursor-pointer"
          >
            <CircleChevron direction={isExpanded ? 'up' : 'down'} />
            <span className="text-m font-bold text-text-primary">
              {isExpanded ? 'Read less' : 'Read more'}
            </span>
          </button>

          {/* Refresh Button - instant client-side random selection */}
          <button
            onClick={handleRefresh}
            className="w-11 h-11 flex items-center justify-center cursor-pointer"
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 20C9.76667 20 7.875 19.225 6.325 17.675C4.775 16.125 4 14.2333 4 12C4 9.76667 4.775 7.875 6.325 6.325C7.875 4.775 9.76667 4 12 4C13.15 4 14.25 4.2375 15.3 4.7125C16.35 5.1875 17.25 5.86667 18 6.75V4H20V11H13V9H17.2C16.6667 8.06667 15.9375 7.33333 15.0125 6.8C14.0875 6.26667 13.0833 6 12 6C10.3333 6 8.91667 6.58333 7.75 7.75C6.58333 8.91667 6 10.3333 6 12C6 13.6667 6.58333 15.0833 7.75 16.25C8.91667 17.4167 10.3333 18 12 18C13.2833 18 14.4417 17.6333 15.475 16.9C16.5083 16.1667 17.2333 15.2 17.65 14H19.75C19.2833 15.7667 18.3333 17.2083 16.9 18.325C15.4667 19.4417 13.8333 20 12 20Z" fill="currentColor"/>
            </svg>
          </button>

          {/* Favorite Button - shows signup dialog */}
          <button
            onClick={() => setShowSignupDialog(true)}
            className="w-11 h-11 flex items-center justify-center cursor-pointer"
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.5 3C14.76 3 13.09 3.81 12 5.09C10.91 3.81 9.24 3 7.5 3C4.42 3 2 5.42 2 8.5C2 12.28 5.4 15.36 10.55 20.04L12 21.35L13.45 20.04C18.6 15.36 22 12.28 22 8.5C22 5.42 19.58 3 16.5 3ZM12.1 18.55L12 18.65L11.9 18.55C7.14 14.24 4 11.39 4 8.5C4 6.5 5.5 5 7.5 5C9.04 5 10.54 5.99 11.07 7.36H12.94C13.46 5.99 14.96 5 16.5 5C18.5 5 20 6.5 20 8.5C20 11.39 16.86 14.24 12.1 18.55Z" fill="currentColor"/>
            </svg>
          </button>

          {/* Share Button */}
          <button
            onClick={handleShare}
            className="w-11 h-11 flex items-center justify-center cursor-pointer"
          >
            <Share size={28} strokeWidth={2} />
          </button>
        </div>

        {/* Link Copied Snackbar - matches app Snackbar component */}
        {showCopiedFeedback && (
          <div
            className="fixed left-0 right-0 z-50 bg-surface-primary border-t-m border-stroke-primary"
            style={{ bottom: 0 }}
          >
            <div className="flex items-center gap-[10px] px-xxl py-xxl">
              {/* Info Icon */}
              <div className="flex items-center shrink-0">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 8V8.01M12 12V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>

              {/* Message */}
              <p className="flex-1 text-m text-text-primary">
                Link copied!
              </p>

              {/* Close Button */}
              <button
                onClick={() => setShowCopiedFeedback(false)}
                className="flex items-center shrink-0 cursor-pointer"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6.4 19L5 17.6L10.6 12L5 6.4L6.4 5L12 10.6L17.6 5L19 6.4L13.4 12L19 17.6L17.6 19L12 13.4L6.4 19Z" fill="currentColor"/>
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Expanded Content */}
        {isExpanded && (
          <div className="text-l text-text-primary prose prose-sm max-w-none pt-m">
            <ReactMarkdown
              components={{
                h2: ({children}) => <h2 className="text-l font-bold mt-l mb-m">{children}</h2>,
                h3: ({children}) => <h3 className="text-l font-bold mt-xl mb-s">{children}</h3>,
                p: ({children}) => <p className="mb-m leading-relaxed">{children}</p>,
                blockquote: ({children}) => <blockquote className="border-l-[4px] border-stroke-light pl-[14px] py-sm my-xl italic">{children}</blockquote>,
                ul: ({children}) => <ul className="list-disc ml-[28px] mb-m">{children}</ul>,
                ol: ({children}) => <ol className="list-decimal ml-[28px] mb-m">{children}</ol>,
                li: ({children}) => <li className="mb-s">{children}</li>
              }}
            >
              {piece.content}
            </ReactMarkdown>
          </div>
        )}
      </div>

      {/* Signup Dialog */}
      <CenteredDialog isOpen={showSignupDialog} onClose={() => setShowSignupDialog(false)}>
        <div className="flex flex-col gap-xxxl">
          {/* Text Section */}
          <div className="flex flex-col gap-m items-center text-center">
            {/* Heart icon */}
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
            </svg>
            <h2 className="text-l font-bold text-text-primary">Save your favorites</h2>
            <p className="text-m text-text-primary">Sign up for Homie to save favorites and get daily inspiration that is 100% algorithm-free.</p>
          </div>

          {/* Buttons Section */}
          <div className="flex flex-col gap-xl">
            {/* Sign Up - primary button */}
            <Link
              href="/signup"
              className="w-full min-h-button-large bg-surface-invert rounded-m px-button-x py-m flex items-center justify-center cursor-pointer"
            >
              <span className="text-l font-bold text-text-invert">Sign up</span>
            </Link>

            {/* About - secondary button */}
            <Link
              href="/"
              className="w-full min-h-button-large bg-surface-primary border-m border-stroke-primary rounded-m px-button-x py-m flex items-center justify-center cursor-pointer"
            >
              <span className="text-l font-bold text-text-primary">About</span>
            </Link>
          </div>
        </div>
      </CenteredDialog>
    </div>
  )
}
